import { useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { apiClient } from '../../../services/api';
import { ProjectMember, Invitation } from '../../../types/domain';

export function useOrganizationTeam() {
  const { currentOrg, projects } = useAuth();
  const { showToast } = useToast();

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isRevokingInvite, setIsRevokingInvite] = useState<string | null>(null);

  // Project assignments & member removal state
  const [managingMember, setManagingMember] = useState<ProjectMember | null>(null);
  const [memberAssignedProjects, setMemberAssignedProjects] = useState<string[]>([]);
  const [isLoadingMemberProjects, setIsLoadingMemberProjects] = useState(false);
  const [isSavingMemberProjects, setIsSavingMemberProjects] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [inviteProjects, setInviteProjects] = useState<string[]>([]);

  const fetchTeamMembers = useCallback(async () => {
    if (!currentOrg?.id) return;
    setIsLoadingMembers(true);
    try {
      let loadedMembers: ProjectMember[] = [];
      try {
        const orgMemRes = await apiClient<{ data?: any[]; members?: any[] }>(
          `/organizations/${currentOrg.id}/members`
        );
        const rawList = orgMemRes.data || orgMemRes.members || [];
        loadedMembers = rawList.map((m: any) => ({
          id: m.id,
          project_id: m.organization_id || currentOrg.id,
          user_id: m.user_id,
          permission_tier: (m.role === 'owner' || m.role === 'admin' ? 'full' : 'edit') as any,
          role: m.role || 'member',
          email: m.email,
          full_name: m.full_name,
          created_at: m.created_at,
          updated_at: m.created_at,
        }));
      } catch (err) {
        console.warn('Organization members endpoint fallback to primary project:', err);
      }

      if (loadedMembers.length === 0 && projects && projects.length > 0) {
        const memRes = await apiClient<{ data?: ProjectMember[]; members?: ProjectMember[] }>(
          `/projects/${projects[0].id}/members`
        );
        loadedMembers = memRes.data || memRes.members || [];
      }
      setMembers(loadedMembers);

      try {
        const invRes = await apiClient<{ data?: Invitation[]; invitations?: Invitation[] }>(
          `/organizations/${currentOrg.id}/invitations`
        );
        setInvitations(invRes.data || invRes.invitations || []);
      } catch (err) {
        setInvitations([]);
      }
    } catch (err) {
      console.warn('Failed to load members:', err);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [currentOrg?.id, projects]);

  const openManageProjects = async (member: ProjectMember) => {
    if (!currentOrg?.id) return;
    setManagingMember(member);
    setIsLoadingMemberProjects(true);
    try {
      const res = await apiClient<{ project_ids?: string[] }>(
        `/organizations/${currentOrg.id}/members/${member.user_id || member.id}/projects`
      );
      setMemberAssignedProjects(res.project_ids || []);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Load Projects',
        message: err?.message || 'Could not fetch member projects.',
      });
      setMemberAssignedProjects([]);
    } finally {
      setIsLoadingMemberProjects(false);
    }
  };

  const handleSaveMemberProjects = async () => {
    if (!currentOrg?.id || !managingMember) return;
    setIsSavingMemberProjects(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}/members/${managingMember.user_id || managingMember.id}/projects`, {
        method: 'PUT',
        body: JSON.stringify({ project_ids: memberAssignedProjects }),
      });
      showToast({
        type: 'success',
        title: 'Projects Assigned',
        message: `Project assignments updated for ${managingMember.full_name || managingMember.email || 'member'}.`,
      });
      setManagingMember(null);
      fetchTeamMembers();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Could not update project assignments.',
      });
    } finally {
      setIsSavingMemberProjects(false);
    }
  };

  const handleExecuteRemoveMember = async () => {
    if (!currentOrg?.id || !memberToRemove) return;
    setIsRemovingMember(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}/members/${memberToRemove.user_id || memberToRemove.id}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Member Removed',
        message: `${memberToRemove.full_name || memberToRemove.email || 'Member'} has been removed from ${currentOrg.name}.`,
      });
      setMemberToRemove(null);
      fetchTeamMembers();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Removal Failed',
        message: err?.message || 'Could not remove member from organization.',
      });
    } finally {
      setIsRemovingMember(false);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !projects || projects.length === 0) return;

    setIsSendingInvite(true);
    try {
      const targetProjId = inviteProjects.length > 0 ? inviteProjects[0] : projects[0].id;
      const res = await apiClient<{
        id: string;
        token: string;
        invite_url?: string;
      }>(`/projects/${targetProjId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole,
          permission_tier: 'full',
        }),
      });
      const url = res.invite_url || `${window.location.origin}/invite/${res.token || res.id}`;
      setGeneratedInviteUrl(url);
      setCopiedLink(false);
      fetchTeamMembers();
      showToast({
        type: 'success',
        title: 'Invitation Created',
        message: `Invite link generated for ${inviteEmail.trim()}.`,
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Send Invitation',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleCopyInviteLink = () => {
    if (generatedInviteUrl) {
      navigator.clipboard.writeText(generatedInviteUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      showToast({
        type: 'info',
        title: 'Copied',
        message: 'Invitation link copied to clipboard.',
      });
    }
  };

  const handleRevokeInvitation = async (inviteId: string) => {
    if (!currentOrg?.id) return;
    setIsRevokingInvite(inviteId);
    try {
      await apiClient(`/organizations/${currentOrg.id}/invitations/${inviteId}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Invitation Revoked',
        message: 'The invitation link is no longer valid.',
      });
      fetchTeamMembers();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Revoke',
        message: err?.message || 'Could not revoke invitation.',
      });
    } finally {
      setIsRevokingInvite(null);
    }
  };

  return {
    members,
    isLoadingMembers,
    fetchTeamMembers,
    invitations,
    inviteModalOpen,
    setInviteModalOpen,
    inviteEmail,
    setInviteEmail,
    inviteRole,
    setInviteRole,
    inviteProjects,
    setInviteProjects,
    isSendingInvite,
    generatedInviteUrl,
    setGeneratedInviteUrl,
    copiedLink,
    handleSendInvite,
    handleCopyInviteLink,
    isRevokingInvite,
    handleRevokeInvitation,
    managingMember,
    setManagingMember,
    memberAssignedProjects,
    setMemberAssignedProjects,
    isLoadingMemberProjects,
    isSavingMemberProjects,
    openManageProjects,
    handleSaveMemberProjects,
    memberToRemove,
    setMemberToRemove,
    isRemovingMember,
    handleExecuteRemoveMember,
  };
}
